/**
 * Inline slide-in drawer for the per-element diff detail in the differences-
 * overview modal. Closed (width 0) until an element is selected; the element
 * list keeps the freed width while browsing. Deliberately NOT a viewport
 * overlay (radix Sheet) — it lives inside the modal.
 */

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { BuildingIfcViewerSections } from "@/lib/i18n/types/buildings-sections/ifc-viewer";
import type { IfcChangeType, IfcElementChange, IfcStoreyRef } from "@/stores/ifcModel/repository";
import { classLabel } from "../disciplineMap";
import { IfcElementDiffDetail } from "../IfcElementDiffDetail";
import { DIFF_SEGMENTS, changeTypeLabel } from "./diffSummaryShared";

interface IfcDiffDetailDrawerProps {
  /** null → drawer closed. */
  change: IfcElementChange | null;
  olderDocId: number;
  newerDocId: number;
  /** Storey lookup from the diff payload — names the containment rows in the
   * detail diff. */
  storeys?: IfcStoreyRef[];
  onClose: () => void;
  tv: BuildingIfcViewerSections;
}

const BADGE_STYLE: Record<IfcChangeType, string> = {
  added: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  removed: "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400",
  modified: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
};

export function IfcDiffDetailDrawer({
  change,
  olderDocId,
  newerDocId,
  storeys,
  onClose,
  tv,
}: IfcDiffDetailDrawerProps) {
  const open = change != null;
  // Keep the last selected change mounted so the content stays visible while
  // the close animation collapses the panel.
  const [rendered, setRendered] = useState(change);
  useEffect(() => {
    if (change) setRendered(change);
  }, [change]);

  // While closed the content stays mounted (for the collapse animation) but is
  // only visually clipped; `inert` removes it from the tab order and the
  // accessibility tree so aria-hidden never covers focusable elements. React 18
  // has no typed `inert` prop, so set it imperatively.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (rootRef.current) rootRef.current.inert = !open;
  }, [open]);

  const dot = rendered ? DIFF_SEGMENTS.find((s) => s.key === rendered.change_type) : null;

  return (
    <div
      ref={rootRef}
      className={cn(
        "flex min-h-0 shrink-0 flex-col overflow-hidden border-border transition-[width] duration-300 ease-in-out",
        open ? "w-[min(420px,38vw)] border-l" : "w-0"
      )}
      aria-hidden={!open}
    >
      {/* Fixed inner width so text doesn't reflow while the panel animates. */}
      <div className="flex min-h-0 w-[min(420px,38vw)] flex-1 flex-col">
        {rendered && (
          <>
            <div className="flex items-center gap-2 px-3 py-2.5">
              <span className={cn("size-2 shrink-0 rounded-full", dot?.bar)} />
              <span
                className="min-w-0 truncate text-sm font-medium"
                title={rendered.name ?? classLabel(rendered.ifc_class)}
              >
                {rendered.name ?? classLabel(rendered.ifc_class)}
              </span>
              <Badge
                variant="outline"
                className={cn(
                  "shrink-0 px-1.5 py-0 text-[10px]",
                  BADGE_STYLE[rendered.change_type]
                )}
              >
                {changeTypeLabel(rendered.change_type, tv)}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto size-7 shrink-0"
                onClick={onClose}
                aria-label={tv.diffCloseDetail}
                title={tv.diffCloseDetail}
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="ifc-glass-scroll min-h-0 flex-1 overflow-y-auto">
              <IfcElementDiffDetail
                baseDocId={olderDocId}
                targetDocId={newerDocId}
                guid={rendered.guid}
                changeType={rendered.change_type}
                storeys={storeys}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
